// embla-init.ts
export interface EmblaApi {
  rootNode(): HTMLElement;
  scrollPrev(): void;
  scrollNext(): void;
  scrollSnapList(): number[];
  scrollTo(index: number): void;
  previousScrollSnap(): number;
  selectedScrollSnap(): number;
  on(event: string, callback: () => void): EmblaApi;
}

/**
 * Initialize the Embla carousel by wiring up navigation buttons and dot controls.
 * @param event - A custom event containing the Embla API instance.
 */
export async function init(event: CustomEvent<EmblaApi>): Promise<void> {
  const emblaApi = event.detail;
  const rootNode = emblaApi.rootNode();

  const prevButton = rootNode.querySelector<HTMLButtonElement>('.embla__prev');
  if (prevButton) {
    prevButton.addEventListener('click', emblaApi.scrollPrev, false);
  }

  const nextButton = rootNode.querySelector<HTMLButtonElement>('.embla__next');
  if (nextButton) {
    nextButton.addEventListener('click', emblaApi.scrollNext, false);
  }

  const dotsContainer = rootNode.querySelector<HTMLElement>('.control-dots .embla__dots');
  if (dotsContainer) {
    const cleanupDots = addDotButtonsWithHandlers(emblaApi, dotsContainer);
    emblaApi.on('destroy', cleanupDots);
  }
}

/**
 * Adds dot buttons for each scroll snap and wires up click handlers.
 * @param emblaApi - Embla carousel API.
 * @param container - Container where dot buttons should be rendered.
 * @returns A function to remove all dot buttons and their event listeners.
 */
export function addDotButtonsWithHandlers(emblaApi: EmblaApi, container: HTMLElement): () => void {
  const totalDots = emblaApi.scrollSnapList().length;
  const clickHandlers: Array<(e: Event) => void> = [];

  // Create dot buttons
  container.innerHTML = Array.from({ length: totalDots })
    .map(() => `<button class="embla__dot" type="button"></button>`)
    .join('');

  const dotButtons = Array.from(container.querySelectorAll<HTMLButtonElement>('.embla__dot'));

  // Attach click handlers
  dotButtons.forEach((button, index) => {
    const handler = () => emblaApi.scrollTo(index);
    clickHandlers.push(handler);
    button.addEventListener('click', handler, false);
  });

  // Update selected dot
  const updateActiveDot = () => {
    const prev = emblaApi.previousScrollSnap();
    const current = emblaApi.selectedScrollSnap();
    dotButtons[prev]?.classList.remove('embla__dot--selected');
    dotButtons[current]?.classList.add('embla__dot--selected');
  };

  // Set initial active state and listen for updates
  updateActiveDot();
  emblaApi.on('reInit', updateActiveDot).on('select', updateActiveDot);

  // Cleanup
  return () => {
    dotButtons.forEach((button, index) => button.removeEventListener('click', clickHandlers[index]));
    container.innerHTML = '';
  };
}
